import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { OrderDetail } from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";
import { ordersAnalyticsApi } from "@/features/orders-analytics/api/orders-analytics.api";
import "@/features/orders-analytics/styles/operation-detail-modal.css";

type Props = {
    operationId: string;
    operationIds: string[];
    onNavigate: (operationId: string) => void;
    onClose: () => void;
    /** FR-025: where keyboard focus goes back to when this dialog closes. */
    restoreFocusTo?: HTMLElement | null;
};

type LoadState =
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; detail: OrderDetail };

export function OperationDetailModal({ operationId, operationIds, onNavigate, onClose, restoreFocusTo }: Props) {
    const [state, setState] = useState<LoadState>({ kind: "loading" });
    const modalRef = useRef<HTMLDivElement | null>(null);

    const index = operationIds.indexOf(operationId);
    const prevId = index > 0 ? operationIds[index - 1] : null;
    const nextId =
        index >= 0 && index < operationIds.length - 1 ? operationIds[index + 1] : null;

    useEffect(() => {
        let cancelled = false;

        setState({ kind: "loading" });

        ordersAnalyticsApi
            .fetchOrderById(operationId)
            .then((detail) => {
                if (cancelled) {
                    return;
                }

                setState({ kind: "ready", detail });
            })
            .catch((reason) => {
                if (cancelled) {
                    return;
                }

                const message =
                    reason instanceof Error ? reason.message : "Could not load operation";
                setState({ kind: "error", message });
            });

        return () => {
            cancelled = true;
        };
    }, [operationId]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
                return;
            }

            if (event.key === "ArrowLeft" && prevId !== null) {
                onNavigate(prevId);
                return;
            }

            if (event.key === "ArrowRight" && nextId !== null) {
                onNavigate(nextId);
                return;
            }

            if (event.key === "Tab") {
                const node = modalRef.current;
                if (node === null) {
                    return;
                }

                const focusable = node.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
                );
                if (focusable.length === 0) {
                    return;
                }

                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                    return;
                }

                if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose, onNavigate, prevId, nextId]);

    useEffect(() => {
        // FR-025: a dialog takes focus when it opens and GIVES IT BACK when it closes.
        // The caller names the trigger explicitly because a row opened with the mouse
        // may never have been the focused element in the first place.
        const previous = restoreFocusTo
            ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
        const node = modalRef.current;
        if (node !== null) {
            node.focus();
        }
        return () => {
            if (previous !== null) previous.focus();
        };
    }, [restoreFocusTo]);

    const kind = state.kind === "ready" ? inferKind(state.detail) : "order";

    return (
        <div
            className="opd-scrim"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                ref={modalRef}
                className="opd opd-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`Detalle de operación ${operationId}`}
                aria-busy={state.kind === "loading"}
                tabIndex={-1}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="opd-bar">
                    <span aria-hidden className="material-symbols-outlined" style={iconMutedStyle}>
                        swap_horiz
                    </span>
                    <span className={kind === "deposit" ? "kind-badge kind-badge--deposit" : "kind-badge kind-badge--order"}>
                        {kind === "deposit" ? "Deposit" : "Order"}
                    </span>
                    <span className="op-id mono">{operationId}</span>
                    <span className="spacer" />
                    <button
                        type="button"
                        className="btn btn--icon"
                        onClick={() => prevId !== null && onNavigate(prevId)}
                        disabled={prevId === null}
                        aria-label="Operación anterior"
                        title="Operación anterior"
                    >
                        <span aria-hidden className="material-symbols-outlined">
                            chevron_left
                        </span>
                    </button>
                    <button
                        type="button"
                        className="btn btn--icon"
                        onClick={() => nextId !== null && onNavigate(nextId)}
                        disabled={nextId === null}
                        aria-label="Operación siguiente"
                        title="Operación siguiente"
                    >
                        <span aria-hidden className="material-symbols-outlined">
                            chevron_right
                        </span>
                    </button>
                    <button
                        type="button"
                        className="btn btn--icon"
                        onClick={onClose}
                        aria-label="Cerrar"
                        title="Cerrar"
                    >
                        <span aria-hidden className="material-symbols-outlined">
                            close
                        </span>
                    </button>
                </div>

                <div className="opd-scroll">
                    {state.kind === "loading" ? (
                        <div className="opd-msg">Cargando…</div>
                    ) : state.kind === "error" ? (
                        <div className="opd-msg err">{state.message}</div>
                    ) : (
                        <ModalBody detail={state.detail} operationId={operationId} kind={kind} />
                    )}
                </div>
            </div>
        </div>
    );
}

function ModalBody({
    detail,
    operationId,
    kind,
}: {
    detail: OrderDetail;
    operationId: string;
    kind: OperationKind;
}) {
    const deposit = detail.deposit;
    const trade = detail.trade;
    const withdraw = detail.withdraw;
    const offramp = detail.offramp;
    const destination = detail.destination;
    const compliance = detail.incomingTransfer !== null ? detail.incomingTransfer.compliance : null;
    const tradingPaths = trade !== null ? trade.tradingPaths : [];

    const senderName = joinNames(detail.user.firstName, detail.user.lastName) ?? detail.counterpartyName;
    const beneficiaryName = destination !== null ? destination.name : null;
    const beneficiaryRail = detail.destinationRail ?? (destination !== null ? destination.type : null);

    const rate =
        detail.publicConversionRatio ??
        detail.originConversionRatio ??
        (trade !== null ? trade.conversionRate : null);
    const destinationReal = detail.actualDestinationAmount ?? detail.destinationAmount;
    const profitIsLoss = detail.profit !== null && detail.profit < 0;
    const kycLabel =
        detail.kyced === true ? "Verificado" : detail.kyced === false ? "No verificado" : null;
    const spreadLabel =
        detail.fxSpreadBps !== null ? `${formatters.formatNumber(detail.fxSpreadBps)} bps` : null;
    const offrampFailed =
        offramp !== null && hasText(offramp.status) && offramp.status.toLowerCase().includes("fail");

    const completedState = classifyStatus(detail.status);
    const timeline: TimelineStepData[] = [
        {
            name: "Creada",
            time: detail.createdAt,
            state: detail.createdAt !== null ? "done" : "pending",
        },
        buildPhaseStep(
            "Inflow recibido",
            detail.inflowStatus ?? (deposit !== null ? deposit.status : null),
            detail.receivedAt ?? (deposit !== null ? deposit.time : null)
        ),
        buildPhaseStep(
            "FX ejecutado",
            trade !== null ? trade.status : null,
            detail.exchangedAt ?? (trade !== null ? trade.time : null)
        ),
        buildPhaseStep(
            "Withdrawal",
            withdraw !== null ? withdraw.status : null,
            detail.withdrawnAt ?? (withdraw !== null ? withdraw.time : null)
        ),
        buildPhaseStep(
            "Offramp",
            offramp !== null ? offramp.status : null,
            offramp !== null ? offramp.time ?? offramp.createdAt : null
        ),
        {
            name: "Completada",
            time: completedState !== "pending" ? detail.updatedAt : null,
            state: completedState,
        },
    ];

    return (
        <div className="layout">
            <div className="col-main">
                <section className="opheader">
                    <div className="opheader__top">
                        <span className={kind === "deposit" ? "kind-badge kind-badge--deposit" : "kind-badge kind-badge--order"}>
                            {kind === "deposit" ? "Deposit" : "Order"}
                        </span>
                        <StatusPill status={detail.status} />
                        <span className="op-id mono">{operationId}</span>
                        <CopyIconButton value={operationId} label="Copiar operation ID" />
                    </div>
                    <div className="flagrow">
                        <FlagChip icon="flag" label="needsReview" tone="review" on={detail.needsReview === true} />
                        <FlagChip icon="pan_tool" label="isManual" tone="manual" on={detail.isManual === true} />
                        <FlagChip icon="science" label="isTest" tone="test" on={detail.isTest === true} />
                        <FlagChip
                            icon="block"
                            label={
                                detail.hasValidationBlocks === true
                                    ? `bloqueos · ${detail.validationBlockCount ?? 0}`
                                    : "sin bloqueos"
                            }
                            tone="block"
                            on={detail.hasValidationBlocks === true}
                        />
                    </div>
                    <div className="opheader__meta">
                        <MetaCell label="Cliente / Workspace" icon="domain" value={detail.client} />
                        <MetaCell label="Canal" value={detail.originTransferMethod} />
                        <MetaCell label="Paso actual" value={detail.currentStep} />
                        <MetaCell
                            label="Creación"
                            value={detail.createdAt !== null ? formatters.formatDateTime(detail.createdAt) : null}
                        />
                        <MetaCell
                            label="Duración total"
                            icon="timer"
                            value={
                                detail.completionTimeMinutes !== null
                                    ? formatters.formatDuration(detail.completionTimeMinutes)
                                    : null
                            }
                        />
                    </div>
                </section>

                <Card icon="route" title="Línea de tiempo" sub="· Flujo de remesa">
                    <Timeline steps={timeline} />
                </Card>

                <Card icon="payments" title="Movimiento de dinero" sub="· Remesa · flujo completo">
                    <div className="money-hero">
                        <div className="money-side">
                            <div className="ml">Envía</div>
                            <div className="mv">{formatAmountNumber(detail.originAmount)}</div>
                            <div className="mccy">{show(detail.originCurrency)}</div>
                        </div>
                        <div className="money-arrow">
                            <span aria-hidden className="material-symbols-outlined">
                                trending_flat
                            </span>
                            {rate !== null ? (
                                <div className="ar-rate">
                                    1 {detail.originCurrency ?? ""} = {formatters.formatNumber(rate)}{" "}
                                    {detail.destinationCurrency ?? ""}
                                </div>
                            ) : null}
                        </div>
                        <div className="money-side recv">
                            <div className="ml">Recibe destino</div>
                            <div className="mv">{formatAmountNumber(destinationReal)}</div>
                            <div className="mccy">{show(detail.destinationCurrency)}</div>
                        </div>
                    </div>

                    <div className="flow">
                        <FlowNode
                            currency={detail.originCurrency}
                            amount={formatMoney(detail.originAmount, detail.originCurrency)}
                            label={detail.depositType}
                            note={detail.inflowProvider}
                        />
                        <FlowConnector />
                        <FlowNode
                            currency={withdraw !== null ? withdraw.currency : null}
                            amount={withdraw !== null ? formatMoney(withdraw.amount, withdraw.currency) : null}
                            label="Conversión FX"
                            note={detail.outflowProvider}
                        />
                        <FlowConnector />
                        <FlowNode
                            currency={offramp !== null ? offramp.currency : detail.destinationCurrency}
                            amount={
                                offramp !== null
                                    ? formatMoney(offramp.amount, offramp.currency)
                                    : formatMoney(destinationReal, detail.destinationCurrency)
                            }
                            label={beneficiaryRail}
                            note={detail.outflowProvider}
                        />
                    </div>

                    <div className="money-row">
                        <MoneyFact label="Par FX" value={detail.pair} />
                        <MoneyFact label="Tasa" value={formatRawNumber(rate)} />
                        <MoneyFact label="Spread" value={spreadLabel} />
                        <MoneyFact
                            label="Destino estimado"
                            value={formatMoney(detail.destinationAmount, detail.destinationCurrency)}
                        />
                        <MoneyFact
                            label="Destino real"
                            value={formatMoney(detail.actualDestinationAmount, detail.destinationCurrency)}
                        />
                        <MoneyFact
                            label="Estado payout"
                            value={offramp !== null ? offramp.status : null}
                            bad={offrampFailed}
                        />
                        <MoneyFact label="Fee total" value={formatMoney(detail.fee, detail.feeCurrency, true)} />
                        <MoneyFact label="Revenue" value={formatMoney(detail.totalRevenue, detail.feeCurrency, true)} />
                        <MoneyFact
                            label="Profit"
                            ok={!profitIsLoss}
                            bad={profitIsLoss}
                            value={formatMoney(detail.profit, detail.feeCurrency, true)}
                        />
                    </div>
                </Card>

                <Card icon="group" title="Participantes" sub="· Remitente y beneficiario">
                    <div className="parts">
                        <div className="part">
                            <div className="part__role">
                                <span className="part__avatar">{initialsFromFull(senderName)}</span>
                                <span className="rt">Remitente</span>
                            </div>
                            <div className="dl">
                                <DlRow label="Nombre" value={senderName} />
                                <DlRow label="Email" value={detail.userEmail} sensitive copyable />
                                <DlRow label="País" value={detail.user.country} />
                                <DlRow label="Tipo de usuario" value={detail.user.userType} />
                                <DlRow label="KYC" value={kycLabel} />
                                <DlRow label="IBAN origen" value={detail.counterpartyIban} sensitive copyable mono />
                                <DlRow label="Account ID" value={detail.counterpartyAccountId} copyable mono />
                            </div>
                        </div>
                        <div className="part">
                            <div className="part__role">
                                <span className="part__avatar">{initialsFromFull(beneficiaryName)}</span>
                                <span className="rt">Beneficiario</span>
                            </div>
                            <div className="dl">
                                <DlRow label="Nombre" value={beneficiaryName} />
                                <DlRow label="Banco" value={destination !== null ? destination.bankName : null} />
                                <DlRow
                                    label="N° de cuenta"
                                    value={destination !== null ? destination.bankAccountNumber : null}
                                    sensitive
                                    copyable
                                    mono
                                />
                                <DlRow
                                    label="Tipo de cuenta"
                                    value={destination !== null ? destination.bankAccountType : null}
                                />
                                <DlRow
                                    label="Tax ID / Cédula"
                                    value={destination !== null ? destination.taxId : null}
                                    sensitive
                                    copyable
                                />
                                <DlRow label="Rail destino" value={beneficiaryRail} />
                            </div>
                        </div>
                    </div>
                </Card>

                <Card icon="hub" title="Proveedores y rails">
                    <div className="kv-grid">
                        <KvRow label="Inflow provider" value={detail.inflowProvider} />
                        <KvRow label="Outflow provider" value={detail.outflowProvider} />
                        <KvRow label="Deposit type" value={detail.depositType} />
                        <KvRow label="Origin method" value={detail.originTransferMethod} />
                        <KvRow label="Destination method" value={detail.destinationTransferMethod} />
                        <KvRow label="Destination rail" value={detail.destinationRail} />

                        <div className="kv-section-title">IDs externos y referencias</div>
                        <KvRow
                            label="Inflow ref"
                            value={detail.incomingTransfer !== null ? detail.incomingTransfer.reference : null}
                            copyable
                            mono
                        />
                        <KvRow label="Reference code" value={deposit !== null ? deposit.referenceCode : null} copyable mono />
                        <KvRow label="Provider ticket" value={offramp !== null ? offramp.ticketId : null} copyable mono />
                        <KvRow label="Offramp ref" value={offramp !== null ? offramp.externalId : null} copyable mono />
                        <KvRow label="Tx hash" value={withdraw !== null ? withdraw.txId : null} sensitive copyable mono />
                        {withdraw !== null && withdraw.txUrlScan !== null ? (
                            <div className="kv-explorer">
                                <a href={withdraw.txUrlScan} target="_blank" rel="noreferrer">
                                    <span aria-hidden className="material-symbols-outlined">
                                        open_in_new
                                    </span>
                                    Ver en block explorer
                                </a>
                            </div>
                        ) : null}
                    </div>
                </Card>

                {tradingPaths.length > 0 ? (
                    <Card icon="alt_route" title="Ruta de conversión" sub="· Legs de trading">
                        <TradingPathList paths={tradingPaths} />
                    </Card>
                ) : null}

                <Card icon="verified_user" title="Compliance" sub="· Screening del inflow">
                    <ComplianceChecks compliance={compliance} />
                </Card>

                <Card icon="account_balance_wallet" title="Fees y rentabilidad">
                    <div className="fees">
                        <FeeRow label="Fee total cobrado" value={formatMoney(detail.fee, detail.feeCurrency)} />
                        <FeeRow label="Fee esperado" value={formatMoney(detail.expectedFee, detail.feeCurrency)} />
                        <FeeRow label="Exchange fee" value={formatMoney(detail.exchangeFee, detail.feeCurrency)} />
                        <FeeRow label="Withdraw fee" value={formatMoney(detail.withdrawFee, detail.feeCurrency)} />
                        <FeeRow label="Gas fee" value={formatRawNumber(detail.gasFee)} />
                        <FeeRow label="Total revenue" value={formatMoney(detail.totalRevenue, detail.feeCurrency)} strong />
                        <FeeRow
                            label="Profit"
                            ok={!profitIsLoss}
                            bad={profitIsLoss}
                            value={formatMoney(detail.profit, detail.feeCurrency)}
                        />
                    </div>
                </Card>

                <TechnicalCard detail={detail} />
            </div>

            <div className="col-rail">
                <div className="card">
                    <div className="card__body">
                        <div className="summary-big">
                            <div className="sl">Monto enviado</div>
                            <div className="sv">
                                {formatAmountNumber(detail.originAmount)}{" "}
                                <span className="sv-ccy">{detail.originCurrency ?? ""}</span>
                            </div>
                        </div>
                        <div className="rail-split">
                            <div>
                                <div className="rl">Revenue</div>
                                <div className="rv">{show(formatMoney(detail.totalRevenue, detail.feeCurrency))}</div>
                            </div>
                            <div>
                                <div className="rl">Profit</div>
                                <div className={profitIsLoss ? "rv bad" : "rv ok"}>
                                    {show(formatMoney(detail.profit, detail.feeCurrency))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card__body">
                        <div className="rail-actions">
                            <button
                                type="button"
                                className="btn btn--brand"
                                disabled
                                aria-label="Reprocesar (deshabilitado)"
                            >
                                <span aria-hidden className="material-symbols-outlined">
                                    restart_alt
                                </span>
                                Reprocesar
                            </button>
                            <CopyTextButton value={operationId} label="Copiar operationId" />
                        </div>
                    </div>
                </div>

                <Card icon="bolt" title="Accesos rápidos">
                    <div className="rail-quick">
                        <RailQuick icon="timeline" label="Eventos / logs" />
                        <RailQuick icon="person" label="Ver cliente" />
                        <RailQuick icon="hub" label="Dashboard proveedor" />
                        <RailQuick icon="download" label="Exportar operación" />
                    </div>
                </Card>
            </div>
        </div>
    );
}

type OperationKind = "order" | "deposit";

type StepState = "done" | "failed" | "pending";

type TimelineStepData = {
    name: string;
    time: number | null;
    state: StepState;
};

const SUCCESS_WORDS = ["success", "complete", "done", "settled", "executed"];
const FAILURE_WORDS = ["fail", "cancel", "reject", "error", "expire", "declin"];

const classifyStatus = (status: string | null | undefined): StepState => {
    if (!hasText(status)) {
        return "pending";
    }

    const lower = status.toLowerCase();

    if (FAILURE_WORDS.some((word) => lower.includes(word))) {
        return "failed";
    }

    if (SUCCESS_WORDS.some((word) => lower.includes(word))) {
        return "done";
    }

    return "pending";
};

const buildPhaseStep = (name: string, status: string | null, time: number | null): TimelineStepData => {
    let state = classifyStatus(status);

    // A leg with no explicit status but a real timestamp did happen.
    if (state === "pending" && time !== null) {
        state = "done";
    }

    return { name, time, state };
};

type TradingPath = NonNullable<OrderDetail["trade"]>["tradingPaths"][number];

type ComplianceData = NonNullable<OrderDetail["incomingTransfer"]>["compliance"];

const COMPLIANCE_LABELS: Record<string, string> = {
    provider: "Provider screening",
    account_limits: "Límites de cuenta",
    payment_method_limits: "Límites de método de pago",
    marble: "Marble (AML)",
    fiat_monitoring: "Monitoreo fiat",
    internal: "Revisión interna",
};

const complianceLabel = (id: string | null, name: string | null): string => {
    const key = id ?? name;

    if (key === null) {
        return "Check";
    }

    const mapped = COMPLIANCE_LABELS[key];
    return mapped !== undefined ? mapped : key;
};

type DataRowProps = {
    label: string;
    value: string | null | undefined;
    sensitive?: boolean;
    copyable?: boolean;
    mono?: boolean;
};

function Card({
    icon,
    title,
    sub,
    children,
}: {
    icon: string;
    title: string;
    sub?: string;
    children: ReactNode;
}) {
    return (
        <section className="card">
            <div className="card__hd">
                <span aria-hidden className="material-symbols-outlined">
                    {icon}
                </span>
                <span className="card__title">{title}</span>
                {sub !== undefined ? <span className="card__sub">{sub}</span> : null}
            </div>
            <div className="card__body">{children}</div>
        </section>
    );
}

function MetaCell({ label, value, icon }: { label: string; value: string | null | undefined; icon?: string }) {
    return (
        <div className="metacell">
            <div className="l">{label}</div>
            <div className="v">
                {icon !== undefined ? (
                    <span aria-hidden className="material-symbols-outlined">
                        {icon}
                    </span>
                ) : null}
                {show(value)}
            </div>
        </div>
    );
}

function StatusPill({ status }: { status: string | null | undefined }) {
    if (!hasText(status)) {
        return (
            <span className="statuspill s-warning">
                <span className="dot" />
                No disponible
            </span>
        );
    }

    return (
        <span className={statusToneClass(status)}>
            <span className="dot" />
            {status}
        </span>
    );
}

function Timeline({ steps }: { steps: TimelineStepData[] }) {
    let previousTime: number | null = null;

    return (
        <div className="timeline">
            {steps.map((step, position) => {
                const duration =
                    step.time !== null && previousTime !== null
                        ? minutesBetween(previousTime, step.time)
                        : null;

                if (step.time !== null) {
                    previousTime = step.time;
                }

                const leftFill =
                    position > 0 && steps[position - 1].state === "done" && step.state === "done";
                const rightFill =
                    position < steps.length - 1 &&
                    step.state === "done" &&
                    steps[position + 1].state === "done";

                const stepClass =
                    step.state === "failed"
                        ? "tstep tstep--failed"
                        : step.state === "done"
                            ? "tstep tstep--done"
                            : "tstep tstep--pending";
                const icon =
                    step.state === "failed" ? "close" : step.state === "done" ? "check" : "more_horiz";

                return (
                    <div key={step.name} className={stepClass}>
                        {position > 0 ? (
                            <span className={leftFill ? "tstep__line tstep__line--l fill" : "tstep__line tstep__line--l"} />
                        ) : null}
                        {position < steps.length - 1 ? (
                            <span className={rightFill ? "tstep__line tstep__line--r fill" : "tstep__line tstep__line--r"} />
                        ) : null}
                        <span className="tstep__dot">
                            <span aria-hidden className="material-symbols-outlined">
                                {icon}
                            </span>
                        </span>
                        <span className="tstep__name">{step.name}</span>
                        <span className="tstep__time">
                            {step.time !== null ? formatters.formatTimestampShort(step.time) : "—"}
                        </span>
                        {duration !== null ? <span className="tstep__dur">+{formatters.formatDuration(duration)}</span> : null}
                    </div>
                );
            })}
        </div>
    );
}

function FlowNode({
    currency,
    amount,
    label,
    note,
}: {
    currency: string | null | undefined;
    amount: string | null | undefined;
    label: string | null | undefined;
    note: string | null | undefined;
}) {
    return (
        <div className="flow-node">
            <span className="fn-ccy">
                {hasText(currency) ? (
                    <>
                        <span className={`ccy-chip ccy-${currency.trim().toUpperCase()}`}>
                            {currency.trim().slice(0, 1)}
                        </span>
                        {currency}
                    </>
                ) : (
                    <span className="missing">N/D</span>
                )}
            </span>
            <div className="fn-amt">{show(amount)}</div>
            <div className="fn-label">{show(label)}</div>
            <div className="fn-note">{show(note)}</div>
        </div>
    );
}

function FlowConnector() {
    return (
        <div className="flow-conn">
            <span aria-hidden className="material-symbols-outlined">
                chevron_right
            </span>
        </div>
    );
}

function MoneyFact({
    label,
    value,
    ok,
    bad,
}: {
    label: string;
    value: string | null;
    ok?: boolean;
    bad?: boolean;
}) {
    const valueClass = bad === true ? "v bad" : ok === true ? "v ok" : "v";

    return (
        <div className="mfact">
            <div className="l">{label}</div>
            <div className={valueClass}>{show(value)}</div>
        </div>
    );
}

function FlagChip({
    icon,
    label,
    tone,
    on,
}: {
    icon: string;
    label: string;
    tone: string;
    on: boolean;
}) {
    const className = on ? `flag flag--on ${tone}` : "flag flag--off";

    return (
        <span className={className}>
            <span aria-hidden className="material-symbols-outlined">
                {icon}
            </span>
            {label}
        </span>
    );
}

function ComplianceChecks({ compliance }: { compliance: ComplianceData }) {
    if (compliance === null || compliance.checks.length === 0) {
        return <div className="empty-note">No disponible</div>;
    }

    return (
        <div className="checks">
            {compliance.checks.map((check, position) => {
                const passed = check.passed === true;
                const failed = check.passed === false;
                const className = passed
                    ? "check check--passed"
                    : failed
                        ? "check check--failed"
                        : "check";
                const icon = passed ? "check" : failed ? "close" : "help";
                const state = passed ? "Passed" : failed ? "Failed" : "No disponible";

                return (
                    <div key={check.id ?? check.name ?? `check-${position}`} className={className}>
                        <span className="check__ic">
                            <span aria-hidden className="material-symbols-outlined">
                                {icon}
                            </span>
                        </span>
                        <div className="check__body">
                            <div className="check__k">{complianceLabel(check.id, check.name)}</div>
                            <div className="check__note">{check.source ?? "—"}</div>
                        </div>
                        <span className="check__state">{state}</span>
                    </div>
                );
            })}
        </div>
    );
}

function TradingPathList({ paths }: { paths: TradingPath[] }) {
    return (
        <div className="route-list">
            {paths.map((path, position) => {
                const executed = path.executed === true;
                const rate = formatRawNumber(path.conversionRate);

                return (
                    <div
                        key={`${path.exchange ?? "leg"}-${position}`}
                        className={executed ? "route-step" : "route-step pending"}
                    >
                        <span>{path.fromCurrency ?? "?"}</span>
                        <span aria-hidden className="material-symbols-outlined">
                            arrow_forward
                        </span>
                        <span>{path.toCurrency ?? "?"}</span>
                        {path.exchange !== null ? <span className="rs-ex">· {path.exchange}</span> : null}
                        {rate !== null ? <span className="rs-rate">@ {rate}</span> : null}
                    </div>
                );
            })}
        </div>
    );
}

function RailQuick({ icon, label }: { icon: string; label: string }) {
    return (
        <button type="button" disabled title="No disponible">
            <span aria-hidden className="material-symbols-outlined">
                {icon}
            </span>
            {label}
            <span className="spacer" />
            <span className="missing">N/D</span>
        </button>
    );
}

function DlRow({ label, value, sensitive, copyable, mono }: DataRowProps) {
    return (
        <div className="dl-row">
            <span className="dk">{label}</span>
            <span className={mono === true ? "dv mono" : "dv"}>
                <FieldValue value={value} sensitive={sensitive} copyable={copyable} copyLabel={label} />
            </span>
        </div>
    );
}

function KvRow({ label, value, sensitive, copyable, mono }: DataRowProps) {
    return (
        <div className="kv">
            <span className="kk">{label}</span>
            <span className={mono === true ? "kvv mono" : "kvv"}>
                <FieldValue value={value} sensitive={sensitive} copyable={copyable} copyLabel={label} />
            </span>
        </div>
    );
}

function FieldValue({
    value,
    sensitive,
    copyable,
    copyLabel,
}: {
    value: string | null | undefined;
    sensitive?: boolean;
    copyable?: boolean;
    copyLabel: string;
}) {
    const [revealed, setRevealed] = useState(false);

    if (!hasText(value)) {
        return <span className="missing">No disponible</span>;
    }

    const isSensitive = sensitive === true;
    const text = isSensitive && !revealed ? maskValue(value) : value;

    return (
        <>
            <span className="truncate">{text}</span>
            {isSensitive ? (
                <button
                    type="button"
                    className="iconbtn"
                    onClick={() => setRevealed((current) => !current)}
                    aria-label={revealed ? `Ocultar ${copyLabel}` : `Mostrar ${copyLabel}`}
                    aria-pressed={revealed}
                    title={revealed ? "Ocultar" : "Mostrar"}
                >
                    <span aria-hidden className="material-symbols-outlined">
                        {revealed ? "visibility_off" : "visibility"}
                    </span>
                </button>
            ) : null}
            {copyable === true ? <CopyIconButton value={value} label={`Copiar ${copyLabel}`} /> : null}
        </>
    );
}

function FeeRow({
    label,
    value,
    ok,
    bad,
    strong,
}: {
    label: string;
    value: string | null;
    ok?: boolean;
    bad?: boolean;
    strong?: boolean;
}) {
    const rowClass = strong === true ? "fee-row strong" : "fee-row";
    const valueClass = bad === true ? "fv bad" : ok === true ? "fv ok" : "fv";

    return (
        <div className={rowClass}>
            <span className="fk">{label}</span>
            <span className={valueClass}>{show(value)}</span>
        </div>
    );
}

function TechnicalCard({ detail }: { detail: OrderDetail }) {
    const [open, setOpen] = useState(false);

    return (
        <section className="card">
            <button
                type="button"
                className={open ? "card__hd collapse__hd open" : "card__hd collapse__hd"}
                onClick={() => setOpen((current) => !current)}
                aria-expanded={open}
            >
                <span aria-hidden className="material-symbols-outlined">
                    data_object
                </span>
                <span className="card__title">Trazabilidad técnica</span>
                <span className="card__sub">· Respuesta cruda del índice</span>
                <span aria-hidden className="material-symbols-outlined chev">
                    expand_more
                </span>
            </button>
            {open ? <pre className="rawjson">{JSON.stringify(detail, null, 2)}</pre> : null}
        </section>
    );
}

function useClipboardCopy() {
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const copy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);

            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };

    return { copied, copy };
}

function CopyIconButton({ value, label }: { value: string; label: string }) {
    const { copied, copy } = useClipboardCopy();

    return (
        <button
            type="button"
            className={copied ? "iconbtn copied" : "iconbtn"}
            onClick={() => copy(value)}
            aria-label={copied ? "Copiado" : label}
            title={copied ? "Copiado" : label}
        >
            <span aria-hidden className="material-symbols-outlined">
                {copied ? "check" : "content_copy"}
            </span>
        </button>
    );
}

function CopyTextButton({ value, label }: { value: string; label: string }) {
    const { copied, copy } = useClipboardCopy();

    return (
        <button type="button" className="btn" onClick={() => copy(value)} aria-label={label}>
            <span aria-hidden className="material-symbols-outlined">
                {copied ? "check" : "content_copy"}
            </span>
            {copied ? "Copiado" : label}
        </button>
    );
}

const statusToneClass = (status: string): string => {
    if (status === "Success") {
        return "statuspill s-ok";
    }

    const lower = status.toLowerCase();
    const isFailure =
        lower.includes("fail") ||
        lower.includes("cancel") ||
        lower.includes("reject") ||
        lower.includes("error") ||
        lower.includes("expire");

    return isFailure ? "statuspill s-critical" : "statuspill s-warning";
};

const inferKind = (detail: OrderDetail): OperationKind => {
    if (detail.operationType === "deposit") {
        return "deposit";
    }

    if (detail.operationType === "order") {
        return "order";
    }

    if (detail.trade !== null || detail.withdraw !== null) {
        return "order";
    }

    if (detail.deposit !== null) {
        return "deposit";
    }

    return "order";
};

// The detail is a CAST of the API response, not a validated shape, so a field the
// backend omits arrives as `undefined` even where the type says `string | null`.
// Every render guard goes through here: one absent field must degrade to the empty
// state, never crash the whole modal on `undefined.trim()`.
const hasText = (value: string | null | undefined): value is string =>
    typeof value === "string" && value.trim().length > 0;

const show = (value: string | null | undefined): ReactNode => {
    if (!hasText(value)) {
        return <span className="missing">No disponible</span>;
    }

    return value;
};

const maskValue = (value: string): string => {
    const trimmed = value.trim();

    if (trimmed.length <= 8) {
        return `${trimmed.slice(0, 2)} •••• ${trimmed.slice(-2)}`;
    }

    return `${trimmed.slice(0, 4)} •••• ${trimmed.slice(-4)}`;
};

const minutesBetween = (from: number, to: number): number => {
    const delta = (to - from) / 60000;
    return delta > 0 ? delta : 0;
};

const initials = (first: string | null | undefined, last: string | null | undefined): string => {
    const a = hasText(first) ? first.trim()[0] : "";
    const b = hasText(last) ? last.trim()[0] : "";
    const joined = `${a}${b}`.toUpperCase();
    return joined.length > 0 ? joined : "—";
};

const initialsFromFull = (full: string | null | undefined): string => {
    if (!hasText(full)) {
        return "—";
    }

    const parts = full.trim().split(/\s+/);
    const first = parts[0];
    const last = parts.length > 1 ? parts[parts.length - 1] : null;
    return initials(first, last);
};

const joinNames = (first: string | null, last: string | null): string | null => {
    const joined = [first, last]
        .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
        .map((part) => part.trim())
        .join(" ");

    return joined.length > 0 ? joined : null;
};

const moneyFormatter2 = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const moneyFormatter4 = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
});

// Detail view shows sub-unit fees/profit, so the 0-decimal dashboard currency
// formatter is too coarse (it turns -0.0043 into "-0"). Use 2 decimals, or 4 for
// values below 1, and normalize negative zero to zero.
const formatAmountNumber = (amount: number): string => {
    const abs = Math.abs(amount);
    const formatter = abs !== 0 && abs < 1 ? moneyFormatter4 : moneyFormatter2;
    const text = formatter.format(amount);

    return /^-0(?:[.,]0+)?$/.test(text) ? formatter.format(0) : text;
};

const formatMoney = (amount: number | null, currency: string | null, hideZero = false): string | null => {
    if (amount === null || !Number.isFinite(amount)) {
        return null;
    }

    if (hideZero && amount === 0) {
        return null;
    }

    const text = formatAmountNumber(amount);
    const trimmedCurrency = typeof currency === "string" ? currency.trim() : "";

    return trimmedCurrency.length > 0 ? `${text} ${trimmedCurrency}` : text;
};

const formatRawNumber = (value: number | null): string | null => {
    if (value === null || !Number.isFinite(value) || value === 0) {
        return null;
    }

    return formatAmountNumber(value);
};

const iconMutedStyle: CSSProperties = { color: "var(--ed-subtle)" };
